import { numberToString, toSuper } from "../../util/string-util.js";
import { Independent } from "./independent.js";
export class Term {
    static g(independents) {
        return new Term(independents);
    }
    independentMap;
    constructor(independentsOrTerm) {
        this.independentMap = {};
        if (independentsOrTerm) {
            if (independentsOrTerm.length !== undefined) {
                const independents = independentsOrTerm;
                if (independents.length > 0) {
                    for (let i = 0; i < independents.length; i++) {
                        const independent = independents[i];
                        if (i == 0 && !independent.isZero()) {
                            this.independentMap[independent.name] = independent.clone();
                        }
                        else {
                            this._mult(independent, false);
                        }
                    }
                    this.normalize();
                }
            }
            else {
                const term = independentsOrTerm;
                Object.keys(term.independentMap).forEach((independentName) => {
                    this.independentMap[independentName] =
                        term.independentMap[independentName].clone();
                });
            }
        }
    }
    /**
     * Clone this term and contained independents.
     * @returns The cloned term
     */
    clone() {
        return new Term(this);
    }
    /**
     * Returns the independent that represents the constant in this term.
     * @returns The constant in this term
     */
    getConstant() {
        if (!this.isZero()) {
            let constant = this.independentMap[Independent.CONSTANT];
            if (constant == null) {
                //no constant present yet, create it (constant = 1)
                constant = Independent.g(1);
                this.independentMap[constant.name] = constant;
            }
            return constant;
        }
        else {
            //we are empty so we return the constant 0
            return Independent.g(0);
        }
    }
    addToConstant(increase) {
        if (this.isZero()) {
            this.independentMap[Independent.CONSTANT] = Independent.g(increase);
        }
        else {
            this.independentMap[Independent.CONSTANT].mult += increase;
        }
        return this.independentMap[Independent.CONSTANT];
    }
    getConstantValue() {
        return this.isZero() ? 0 : this.independentMap[Independent.CONSTANT].mult;
    }
    /**
     * This multiplies the constant with the multiplier of all independents.
     * The independents will be normalized as well. E.g. if the set of independents
     * would be 3∙4x∙2y^2 will be transformed to 24∙x∙y^2
     */
    normalize() {
        if (!this.isZero()) {
            const constant = this.getConstant();
            Object.values(this.independentMap).forEach((independent) => {
                if (!independent.isConstant()) {
                    constant.mult *= independent.normalize();
                }
            });
        }
    }
    /**
     * Removes all independents which makes this term 0.
     */
    makeZero() {
        this.independentMap = {};
    }
    /**
     * Test whether this term is zero
     * @returns true when term is empy, i.e. equals 0
     */
    isZero() {
        return Object.keys(this.independentMap).length == 0;
    }
    /**
     * Test whether we are a constant
     * @returns true when we only have a constant
     */
    isConstant() {
        return Object.keys(this.independentMap).length == 1;
    }
    /**
     * Inverts the sign of this term when not zero.
     * @returns the now inverted term itself
     */
    invert() {
        if (!this.isZero()) {
            this.getConstant().invert();
        }
        return this;
    }
    /**
     * Gets the independent with given name.
     * @param independentName The name of the independent to return
     * @returns the independent with given name, undefined otherwise
     */
    independent(independentName) {
        return this.independentMap[independentName];
    }
    /**
     * Gets all independents
     * @returns all independents within this term
     */
    independents() {
        return Object.values(this.independentMap);
    }
    /**
     * Removes the independent with given name from this term and returns
     * the removed independent.
     * @param independentName The name of the independent to remove;
     * @returns The removed independent, undefined otherwise
     */
    removeIndependent(independentName) {
        const independent = this.independent(independentName);
        if (independent) {
            delete this.independentMap[independentName];
        }
        return independent;
    }
    mult(independents) {
        if (!this.isZero()) {
            // we do not represent a 0
            let _independents = [];
            if (independents.length !== undefined) {
                _independents = independents;
            }
            else if (independents.independentMap !== undefined) {
                _independents = Object.values(independents.independentMap);
            }
            else {
                _independents = [independents];
            }
            _independents.forEach((independent) => {
                this._mult(independent, true);
            });
            this.normalize();
        }
        return this;
    }
    /**
     * Multiply this term with the given independent. If the independent
     * is not yet present, it is simply added to the set of independents.
     * When the independent is zero, the independents in this term are removed.
     * When the independent is already present, the internal independent is
     * updated: i.e. powers are added and multipliers are multiplied.
     * @param independent The independent where to multiply this term to.
     * @param cloneIndependent whether to clone the given independent
     * @returns the now multiplied term itself
     */
    _mult(independent, cloneIndependent) {
        if (independent.isZero()) {
            // term * 0 = 0
            this.makeZero();
        }
        if (!this.isZero()) {
            // we do not represent a 0
            const _independent = this.independentMap[independent.name];
            if (_independent == null) {
                // add independent
                this.independentMap[independent.name] = cloneIndependent
                    ? independent.clone()
                    : independent;
            }
            else {
                // update independent
                if (!_independent.isConstant()) {
                    _independent.pow += independent.pow;
                }
                _independent.mult *= independent.mult;
            }
        }
        return this;
    }
    /**
     * Solves the term by substituting all independents with their
     * given values. An error is thrown when a value for an independent
     * is missing.
     * @param vars The values for the independents.
     * @returns The value for this term with given independent values.
     */
    solve(vars) {
        let result = this.isZero() ? 0.0 : this.getConstant().mult;
        Object.values(this.independentMap).forEach((independent) => {
            if (!independent.isConstant()) {
                const value = vars[independent.name];
                if (value == null)
                    throw new Error(`No value found for independent ${independent.name}.`);
                result *= Math.pow(value, independent.pow);
            }
        });
        return result;
    }
    toSourceCode(forceSign = false, emptyOnZero = false) {
        return this.toText(forceSign, emptyOnZero, "*", false, (pow) => `_p${pow}`);
    }
    toString(forceSign = false, emptyOnZero = false) {
        return this.toText(forceSign, emptyOnZero, "∙", false, toSuper);
    }
    toText(forceSign, emptyOnZero, multiplicationSign, forceNoneInteger, toPower) {
        let sb = "";
        if (this.isZero()) {
            if (!emptyOnZero) {
                if (forceSign) {
                    sb += "+";
                }
                sb += numberToString(0, forceNoneInteger);
                ;
            }
        }
        else {
            const constant = this.getConstant();
            const emptyOnOne = !this.isConstant();
            sb += constant.toText(forceSign, emptyOnOne, forceNoneInteger, toPower);
            let insertDelimiter = Math.abs(this.getConstantValue()) != 1 || !emptyOnOne;
            Object.values(this.independentMap)
                .sort((i0, i1) => {
                const powerOrder = i1.pow - i0.pow;
                if (powerOrder != 0)
                    return powerOrder;
                return i0.name.localeCompare(i1.name, undefined, { numeric: true });
            })
                .forEach((independent) => {
                if (!independent.isConstant()) {
                    if (insertDelimiter) {
                        sb += multiplicationSign;
                    }
                    else {
                        insertDelimiter = true;
                    }
                    sb += independent.toText(false, true, forceNoneInteger, toPower);
                }
            });
        }
        return sb;
    }
    getOperationsHistogram() {
        const histogram = {};
        const independents = Object.values(this.independentMap);
        let multiplications = independents.length - 1;
        Object.values(this.independentMap).forEach((independent) => {
            if (independent.isOne() && multiplications > 0) {
                multiplications--;
            }
            else {
                const iOp = independent.getOperationsHistogram();
                Object.keys(iOp).forEach((opKey) => {
                    if (histogram[opKey] === undefined) {
                        histogram[opKey] = iOp[opKey];
                    }
                    else {
                        histogram[opKey] += iOp[opKey];
                    }
                });
            }
        });
        if (multiplications > 0) {
            histogram["*"] = multiplications;
        }
        return histogram;
    }
}
